/** Announcements: agent feed page + slim banner for the newest unread one. */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Megaphone, X } from 'lucide-react';
import api from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
export const bannerUrl = (id) => `${API}/api/announcements/images/${id}`;

export function AnnouncementCard({ a, onOpen }) {
  return (
    <article className={`overflow-hidden rounded-2xl border bg-card ${a.read === false ? 'ring-2 ring-primary/30' : ''}`} onMouseEnter={onOpen} data-testid={`announcement-${a.announcement_id}`}>
      {a.banner_image_id && <img src={bannerUrl(a.banner_image_id)} alt="" className="aspect-[3/1] w-full object-cover" />}
      <div className="space-y-2 p-5">
        <p className="text-xs text-muted-foreground">{a.published_at ? new Date(a.published_at).toLocaleDateString() : 'Draft preview'}{a.read === false ? ' · new' : ''}</p>
        <h2 className="text-lg font-semibold">{a.title}</h2>
        <div className="prose prose-sm max-w-none text-sm [&_li]:ml-4 [&_ul]:list-disc" dangerouslySetInnerHTML={{ __html: a.body_html }} />
      </div>
    </article>
  );
}

export function AnnouncementBanner() {
  const [a, setA] = useState(null);
  const location = useLocation();
  useEffect(() => { api.request('/announcements').then((d) => setA(d.announcements.find((x) => !x.read) || null)).catch(() => {}); }, [location.pathname]);
  if (!a || location.pathname === '/announcements') return null;
  const dismiss = () => { api.request(`/announcements/${a.announcement_id}/read`, { method: 'POST' }).catch(() => {}); setA(null); };
  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2 text-sm" data-testid="announcement-banner">
      <Megaphone className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0 flex-1 truncate"><b>{a.title}</b></span>
      <Link to="/announcements" className="shrink-0 font-medium text-primary hover:underline" data-testid="announcement-banner-read">Read</Link>
      <button type="button" aria-label="Dismiss" onClick={dismiss} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid="announcement-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function AnnouncementsPage() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.request('/announcements').then((d) => setItems(d.announcements)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const open = (a) => { if (!a.read) { setItems((xs) => xs.map((x) => (x.announcement_id === a.announcement_id ? { ...x, read: true } : x))); api.request(`/announcements/${a.announcement_id}/read`, { method: 'POST' }).catch(() => {}); } };
  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-24" data-testid="announcements-page">
      <div><p className="text-xs uppercase tracking-wide text-muted-foreground">MSC</p><h1 className="text-3xl font-bold">Announcements</h1></div>
      {!items ? <p className="text-sm text-muted-foreground">Loading…</p> : !items.length ? <p className="text-sm text-muted-foreground" data-testid="announcements-empty">No announcements yet.</p> :
        items.map((a) => <AnnouncementCard key={a.announcement_id} a={a} onOpen={() => open(a)} />)}
    </div>
  );
}
